import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../config/firebase';

interface Payment {
  id: string;
  date: any;
  method: string;
  amount: number;
  convenienceFee?: number;
  stripePaymentId?: string;
  referenceNote?: string;
  refunded?: boolean;
}

interface RefundModalProps {
  show: boolean;
  onHide: () => void;
  payment: Payment | null;
  registrationId: string;
  onRefundComplete: () => void;
}

const RefundModal: React.FC<RefundModalProps> = ({
  show,
  onHide,
  payment,
  registrationId,
  onRefundComplete
}) => {
  const [reason, setReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCreditCard = payment?.method === 'CreditCard' && !!payment?.stripePaymentId;
  const totalAmount = payment ? Number(payment.amount) || 0 : 0;

  // Calculate days since payment for 180-day warning
  const getDaysSincePayment = (): number => {
    if (!payment?.date) return 0;
    const paymentDate = payment.date?.toDate ? payment.date.toDate() : new Date(payment.date);
    return Math.floor((Date.now() - paymentDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const daysSincePayment = getDaysSincePayment();
  const isExpired = isCreditCard && daysSincePayment > 180;
  const isNearExpiry = isCreditCard && daysSincePayment > 150 && daysSincePayment <= 180;

  const formatDate = (date: any): string => {
    if (!date) return 'Unknown';
    try {
      if (date?.toDate) return date.toDate().toLocaleDateString();
      return new Date(date).toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const handleRefund = async () => {
    if (!payment || !reason.trim()) {
      setError('Please provide a reason for the refund');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      if (isCreditCard) {
        // Process Stripe refund
        const stripeRefund = httpsCallable(functions, 'stripeRefund');
        await stripeRefund({
          paymentId: payment.id,
          reason: reason.trim(),
          registrationId
        });
      } else {
        // Record manual refund
        const recordManualRefund = httpsCallable(functions, 'recordManualRefund');
        await recordManualRefund({
          paymentId: payment.id,
          reason: reason.trim(),
          registrationId
        });
      }

      // Success - close modal and refresh
      setReason('');
      onRefundComplete();
      onHide();
    } catch (err: any) {
      console.error('Refund error:', err);
      // Extract the actual error message from Firebase callable function errors
      const errorMessage = err?.details?.message || err?.message || 'Failed to process refund';
      setError(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    if (!processing) {
      setReason('');
      setError(null);
      onHide();
    }
  };

  if (!payment) return null;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isCreditCard ? 'Refund Credit Card Payment' : 'Record Manual Refund'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}

        {isExpired && (
          <Alert variant="danger">
            <strong>Cannot Refund:</strong> This payment is {daysSincePayment} days old. 
            Stripe only allows refunds within 180 days of the original payment.
          </Alert>
        )}

        {isNearExpiry && !isExpired && (
          <Alert variant="warning">
            <strong>Warning:</strong> This payment is {daysSincePayment} days old. 
            Stripe refunds must be processed within 180 days. Only {180 - daysSincePayment} days remaining.
          </Alert>
        )}

        <div className="mb-3">
          <h6>Payment Details</h6>
          <table className="table table-sm">
            <tbody>
              <tr>
                <td><strong>Date:</strong></td>
                <td>{formatDate(payment.date)}</td>
              </tr>
              <tr>
                <td><strong>Method:</strong></td>
                <td>{payment.method}</td>
              </tr>
              <tr>
                <td><strong>Amount:</strong></td>
                <td>${payment.amount.toFixed(2)}</td>
              </tr>
              {payment.convenienceFee && payment.convenienceFee > 0 && (
                <tr>
                  <td><strong>Convenience Fee:</strong></td>
                  <td>${payment.convenienceFee.toFixed(2)}</td>
                </tr>
              )}
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <td><strong>Total Refund:</strong></td>
                <td><strong>${totalAmount.toFixed(2)}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>

        <Form.Group className="mb-3">
          <Form.Label><strong>Reason for Refund *</strong></Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter the reason for this refund..."
            disabled={processing || isExpired}
          />
          <Form.Text className="text-muted">
            This will be recorded in the payment history and visible to admins.
          </Form.Text>
        </Form.Group>

        {isCreditCard && !isExpired && (
          <Alert variant="info">
            <small>
              This will refund <strong>${totalAmount.toFixed(2)}</strong> to the original credit card. 
              An email confirmation will be sent to the camper.
            </small>
          </Alert>
        )}

        {!isCreditCard && (
          <Alert variant="info">
            <small>
              This will record a manual refund of <strong>${payment.amount.toFixed(2)}</strong>. 
              You'll need to process the actual refund outside the system (e.g., write a check, send e-transfer).
              An email confirmation will be sent to the camper.
            </small>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={processing}>
          Cancel
        </Button>
        <Button 
          variant="danger" 
          onClick={handleRefund} 
          disabled={processing || !reason.trim() || isExpired}
        >
          {processing ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              Processing...
            </>
          ) : (
            `Refund $${totalAmount.toFixed(2)}`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RefundModal;
